import * as React from 'react';
import { getUserApi, signUpApi } from '../api/api';
import { useNavigate } from 'react-router-dom';
import BtnFilled from '../components/BtnFilled';
import Modal from '../components/Modal';
import PersonalData from '../components/PersonalData';
import TermsOfUse from '../components/TermsOfUse';
import { ControlledCheckbox } from '../components/Checkbox';
import ControlledInput from '../components/ControlledInput';
import { FormProvider, useForm } from 'react-hook-form';
import { useCallback, useState } from 'react';
import { useUserStore } from '../stores';
import ControlledInputPassword from '../components/ControlledInputPassword';
import { validRussianEmail } from '../regex';
import { ERROR_TEXT } from '../constants';
import ControlledDropdown from '../components/ControlledDropdown';
import { useOrganizations } from '../features/Organizations/hooks/useOrganizations';

const Registration = () => {
  const { setUser, setAuthorized } = useUserStore();
  const { organizations } = useOrganizations();
  const navigate = useNavigate();
  const [_res, _setRes] = React.useState({});

  const methods = useForm({ mode: 'onTouched' });

  /** initial data: **/
  const inputsFields = [
    { name: 'fio', label: 'Фамилия Имя и Отчество', placeholder: 'Иванов Иван Иванович', type: 'text', maxLength: 80 },
    { name: 'phone', label: 'Номер телефона', placeholder: '+7 945 564 923 56', type: 'text', mask: '+7 999 999 99 99' },
    { name: 'email', label: 'Email', placeholder: 'someone@example.com', type: 'email', maxLength: 255 },
    { name: 'password', label: 'Пароль', placeholder: '********', type: 'password' },
  ];

  /** for checkboxes with agreement: **/
  const [showModalPersonalData, setShowModalPersonalData] = React.useState(false);
  const [showModalTermsOfUse, setShowModalTermsOfUse] = React.useState(false);

  const personalData = methods.watch('personalData');
  const termsOfUse = methods.watch('termsOfUse');

  const renderAgreementLabel = (text, highlightLink, onClick) => (
    <>
      {text}{' '}
      <button onClick={onClick} className="inline text-orange-500 underline cursor-pointer">
        {highlightLink}
      </button>
    </>
  );

  /** actions: **/

  const handleSubmit = useCallback(
    async (data) => {
      const { termsOfUse, personalData, phone, ...fields } = data;

      if (!validRussianEmail.test(fields.email)) {
        _setRes({ error: 'Для регистрации в системе MIRA допустимы только email с российскими доменами' });
        return;
      }

      const userData = {
        user: { role: 'customer', ...fields, phone: phone.replace(/\s+/g, '') },
        redirect_link: process.env.REACT_APP_REDIRECT_LINK + 'verify',
      };

      await signUpApi(
        userData,
        async () => {
          window.location.reload();
        },
        (err) => {
          _setRes(err);
        }
      );

      await getUserApi(
        (data) => {
          setUser(data);
          setAuthorized(true);
          navigate('/user');
        },
        (err) => {
          setAuthorized(false);
        }
      );
    },
    [_setRes, setUser, setAuthorized, navigate]
  );

  const [isPasswordValid, setIsPasswordValid] = useState(false);

  return (
    <div className="w-full max-w-lg">
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(handleSubmit)} className="bg-white shadow-2xl rounded-b-3xl border-b border-x border-gray-300 px-8 pt-6 pb-8 mb-4">
          {inputsFields.map((input) => {
            const isPassword = input.type === 'password';

            if (isPassword) {
              return (
                <ControlledInputPassword
                  rules={{ required: ERROR_TEXT.REQUIRED }}
                  key={input.name}
                  showTips={true}
                  handleTipsPassword={setIsPasswordValid}
                  classContainer={'mb-4'}
                  {...input}
                />
              );
            }

            return <ControlledInput rules={{ required: ERROR_TEXT.REQUIRED }} key={input.name} classContainer={'mb-4'} {...input} />;
          })}
          <ControlledDropdown
            name="organization_id"
            valkey="id"
            optkey="username"
            label="Выберите организацию"
            options={organizations}
            rules={{ required: ERROR_TEXT.REQUIRED }}
            className="mb-4"
            placeholder="Выберите организацию"
            defaultValue={1}
          />

          {_res && <p className="mb-4 text-red-500 p-0 font-bold text-xs italic">{_res.error}</p>}

          <ControlledCheckbox
            control={methods.control}
            name={'personalData'}
            label={renderAgreementLabel('Я соглашаюсь на обработку', 'Персональных данных', () => setShowModalPersonalData(true))}
          />

          <ControlledCheckbox
            control={methods.control}
            name={'termsOfUse'}
            label={renderAgreementLabel('Я принимаю условия', 'Пользовательского соглашения', () => setShowModalTermsOfUse(true))}
          />

          <BtnFilled disabled={!personalData || !termsOfUse || !isPasswordValid} className={'my-4'} type={'submit'} title="Зарегистрироваться" />
        </form>
      </FormProvider>

      <Modal
        showModal={showModalPersonalData}
        setShowModal={setShowModalPersonalData}
        title="Политика в отношении обработки персональных данных"
        showSave={false}
        error={null}
        classWidth="relative w-11/12 lg:w-1/2 my-6 mx-2">
        <PersonalData />
      </Modal>

      <Modal
        showModal={showModalTermsOfUse}
        setShowModal={setShowModalTermsOfUse}
        title="Пользовательское соглашение"
        showSave={false}
        error={null}
        classWidth="relative w-11/12 lg:w-1/2 my-6 mx-2">
        <TermsOfUse />
      </Modal>
    </div>
  );
};

export default Registration;
